import React, { useState, useCallback } from "react";
import { SOAPNoteCopyMarkdown } from "../../../services/encountersService";

interface SOAPNoteDisplayProps {
  soap: {
    subjective: any;
    objective: any;
    assessment: any;
    plan: any;
  };
  copyMarkdown?: SOAPNoteCopyMarkdown;
  onCopied?: (label: string) => void;
}

type SectionKey = "subjective" | "objective" | "assessment" | "plan";

const SOAPNoteDisplay: React.FC<SOAPNoteDisplayProps> = ({
  soap,
  copyMarkdown,
  onCopied,
}) => {
  const [copiedSection, setCopiedSection] = useState<SectionKey | null>(null);

  const parseContent = (content: any) => {
    if (typeof content === "object" && content !== null) return content;
    if (typeof content === "string") {
      try {
        return JSON.parse(content);
      } catch {
        // Try replacing single quotes with double quotes as a last resort
        try {
          return JSON.parse(content.replace(/'/g, '"'));
        } catch {
          return content;
        }
      }
    }
    return content;
  };

  const subjectiveData = parseContent(soap.subjective);
  const objectiveData = parseContent(soap.objective);
  const assessmentData = parseContent(soap.assessment);
  const planData = parseContent(soap.plan);

  const buildLocalMarkdown = useCallback(
    (section: SectionKey): string => {
      const field = (label: string, value: any) =>
        value && value !== "Not documented" ? `${label}: ${value}` : "";
      const list = (label: string, items: any[]) =>
        items?.length
          ? `${label}:\n${items.map((i) => `• ${i}`).join("\n")}`
          : "";

      if (section === "subjective") {
        const d = subjectiveData;
        if (typeof d === "string") return `SUBJECTIVE\n\n${d}`;
        const parts = [
          field("Chief Complaint", d.chief_complaint),
          field("History of Present Illness", d.history_of_present_illness),
          field("Past Medical History", d.past_medical_history),
          field("Medications & Allergies", d.medications_allergies),
          field("Social History", d.social_history),
          field("Family History", d.family_history),
          field("Review of Systems", d.review_of_systems),
        ].filter(Boolean);
        return `SUBJECTIVE\n\n${parts.join("\n\n")}`;
      }
      if (section === "objective") {
        const d = objectiveData;
        if (typeof d === "string") return `OBJECTIVE\n\n${d}`;
        const parts = [
          field("Vital Signs", d.vital_signs),
          field("General Survey", d.general_survey),
        ];
        if (d.physical_exam) {
          const examParts = ["Physical Examination:"];
          const subFields: [string, string][] = [
            ["heent", "HEENT"],
            ["neck", "Neck"],
            ["chest_lungs", "Chest/Lungs"],
            ["cardiovascular", "Cardiovascular"],
            ["abdomen", "Abdomen"],
            ["extremities", "Extremities"],
            ["neurological", "Neurological"],
            ["skin", "Skin"],
          ];
          subFields.forEach(([key, label]) => {
            const v = d.physical_exam[key];
            if (v && v !== "Not documented")
              examParts.push(`  • ${label}: ${v}`);
          });
          if (examParts.length > 1) parts.push(examParts.join("\n"));
        }
        parts.push(field("Diagnostic Data", d.diagnostic_data));
        return `OBJECTIVE\n\n${parts.filter(Boolean).join("\n\n")}`;
      }
      if (section === "assessment") {
        const d = assessmentData;
        if (typeof d === "string") return `ASSESSMENT\n\n${d}`;
        const parts = [
          field("Summary", d.summary_statement),
          field("Primary Diagnosis", d.primary_diagnosis),
          list("Differential Diagnoses", d.differential_diagnoses),
          list("Other Active Issues", d.other_active_issues),
        ].filter(Boolean);
        return `ASSESSMENT\n\n${parts.join("\n\n")}`;
      }
      if (section === "plan") {
        const d = planData;
        if (typeof d === "string") return `PLAN\n\n${d}`;
        const parts = [
          list("Tests to Order", d.tests_to_order),
          list("Medications", d.medications),
          field("Supportive Care Instructions", d.supportive_care_instructions),
          list("Procedures", d.procedures),
          field("Patient Education", d.education),
          field("Follow-Up", d.follow_up),
        ].filter(Boolean);
        return `PLAN\n\n${parts.join("\n\n")}`;
      }
      return "";
    },
    [subjectiveData, objectiveData, assessmentData, planData],
  );

  const handleCopySection = useCallback(
    async (section: SectionKey) => {
      const text = copyMarkdown?.[section] || buildLocalMarkdown(section);
      try {
        await navigator.clipboard.writeText(text);
        setCopiedSection(section);
        onCopied?.(section.charAt(0).toUpperCase() + section.slice(1));
        setTimeout(() => setCopiedSection(null), 2000);
      } catch {
        // Clipboard API unavailable
      }
    },
    [copyMarkdown, buildLocalMarkdown, onCopied],
  );

  const CopyButton: React.FC<{ section: SectionKey }> = ({ section }) => (
    <button
      onClick={() => handleCopySection(section)}
      title={`Copy ${section} section`}
      className="ml-auto flex items-center gap-1 text-xs px-2 py-1 rounded-md border transition-colors
        border-gray-200 text-gray-400 hover:text-gray-700 hover:border-gray-400
        focus:outline-none focus:ring-1 focus:ring-blue-400"
    >
      {copiedSection === section ? (
        <>
          <svg
            className="w-3.5 h-3.5 text-green-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M5 13l4 4L19 7"
            />
          </svg>
          <span className="text-green-500 font-medium">Copied</span>
        </>
      ) : (
        <>
          <svg
            className="w-3.5 h-3.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
          Copy
        </>
      )}
    </button>
  );

  const renderField = (label: string, value: any, indent: boolean = false) => {
    if (!value || value === "Not documented") return null;
    return (
      <div className={`mb-3 ${indent ? "ml-4" : ""}`}>
        <span className="font-semibold text-gray-700">{label}:</span>
        <span className="ml-2 text-gray-900">{value}</span>
      </div>
    );
  };

  const renderList = (label: string, items: any[]) => {
    if (!items || items.length === 0) return null;
    return (
      <div className="mb-3">
        <div className="font-semibold text-gray-700 mb-2">{label}:</div>
        <ul className="list-disc list-inside ml-4 space-y-1">
          {items.map((item, index) => (
            <li key={index} className="text-gray-900">
              {item}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Subjective Section */}
      <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-blue-500">
        <div className="flex items-center mb-4">
          <h3 className="text-2xl font-bold text-blue-600">Subjective</h3>
          <CopyButton section="subjective" />
        </div>
        <div className="space-y-2">
          {typeof subjectiveData === "string" ? (
            <div className="text-gray-900 whitespace-pre-wrap">
              {subjectiveData}
            </div>
          ) : (
            <>
              {renderField("Chief Complaint", subjectiveData.chief_complaint)}
              {renderField(
                "History of Present Illness",
                subjectiveData.history_of_present_illness,
              )}
              {renderField(
                "Past Medical History",
                subjectiveData.past_medical_history,
              )}
              {renderField(
                "Medications & Allergies",
                subjectiveData.medications_allergies,
              )}
              {renderField("Social History", subjectiveData.social_history)}
              {renderField("Family History", subjectiveData.family_history)}
              {renderField(
                "Review of Systems",
                subjectiveData.review_of_systems,
              )}
            </>
          )}
        </div>
      </div>

      {/* Objective Section */}
      <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-green-500">
        <div className="flex items-center mb-4">
          <h3 className="text-2xl font-bold text-green-600">Objective</h3>
          <CopyButton section="objective" />
        </div>
        <div className="space-y-2">
          {typeof objectiveData === "string" ? (
            <div className="text-gray-900 whitespace-pre-wrap">
              {objectiveData}
            </div>
          ) : (
            <>
              {renderField("Vital Signs", objectiveData.vital_signs)}
              {renderField("General Survey", objectiveData.general_survey)}
              {objectiveData.physical_exam && (
                <div className="mb-3">
                  <div className="font-semibold text-gray-700 mb-2">
                    Physical Examination:
                  </div>
                  <div className="ml-4 space-y-1">
                    {renderField(
                      "HEENT",
                      objectiveData.physical_exam.heent,
                      true,
                    )}
                    {renderField(
                      "Neck",
                      objectiveData.physical_exam.neck,
                      true,
                    )}
                    {renderField(
                      "Chest/Lungs",
                      objectiveData.physical_exam.chest_lungs,
                      true,
                    )}
                    {renderField(
                      "Cardiovascular",
                      objectiveData.physical_exam.cardiovascular,
                      true,
                    )}
                    {renderField(
                      "Abdomen",
                      objectiveData.physical_exam.abdomen,
                      true,
                    )}
                    {renderField(
                      "Extremities",
                      objectiveData.physical_exam.extremities,
                      true,
                    )}
                    {renderField(
                      "Neurological",
                      objectiveData.physical_exam.neurological,
                      true,
                    )}
                    {renderField(
                      "Skin",
                      objectiveData.physical_exam.skin,
                      true,
                    )}
                  </div>
                </div>
              )}
              {renderField("Diagnostic Data", objectiveData.diagnostic_data)}
            </>
          )}
        </div>
      </div>

      {/* Assessment Section */}
      <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-yellow-500">
        <div className="flex items-center mb-4">
          <h3 className="text-2xl font-bold text-yellow-600">Assessment</h3>
          <CopyButton section="assessment" />
        </div>
        <div className="space-y-2">
          {typeof assessmentData === "string" ? (
            <div className="text-gray-900 whitespace-pre-wrap">
              {assessmentData}
            </div>
          ) : (
            <>
              {renderField("Summary", assessmentData.summary_statement)}
              {renderField(
                "Primary Diagnosis",
                assessmentData.primary_diagnosis,
              )}
              {renderList(
                "Differential Diagnoses",
                assessmentData.differential_diagnoses,
              )}
              {renderList(
                "Other Active Issues",
                assessmentData.other_active_issues,
              )}
            </>
          )}
        </div>
      </div>

      {/* Plan Section */}
      <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-purple-500">
        <div className="flex items-center mb-4">
          <h3 className="text-2xl font-bold text-purple-600">Plan</h3>
          <CopyButton section="plan" />
        </div>
        <div className="space-y-2">
          {typeof planData === "string" ? (
            <div className="text-gray-900 whitespace-pre-wrap">{planData}</div>
          ) : (
            <>
              {renderList("Tests to Order", planData.tests_to_order)}
              {renderList("Medications", planData.medications)}
              {renderField(
                "Supportive Care Instructions",
                planData.supportive_care_instructions,
              )}
              {renderList("Procedures", planData.procedures)}
              {renderField("Education", planData.education)}
              {renderField("Follow-up", planData.follow_up)}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SOAPNoteDisplay;
